import React, { createContext, useContext, useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import jwt_decode from 'jwt-decode';
import apiService from '../services/apiService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasComplianceAccess, setHasComplianceAccess] = useState(false);
  const [hasComplianceLicense, setHasComplianceLicense] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(false);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkComplianceAccess = async (token) => {
    if (!token) return false;
    
    try {
      setCheckingAccess(true);
      const response = await apiService.checkComplianceAccess(token);
      const hasAccess = response.has_access || false;
      const hasLicense = response.has_license || false;
      setHasComplianceAccess(hasAccess);
      setHasComplianceLicense(hasLicense);
      return hasAccess;
    } catch (error) {
      console.error('Error checking compliance access:', error);
      setHasComplianceAccess(false);
      setHasComplianceLicense(false);
      return false;
    } finally {
      setCheckingAccess(false);
    }
  };

  const checkAuthStatus = async () => {
    try {
      const token = Cookies.get('login_jwt');
      if (token) {
        const decoded = jwt_decode(token);
        const currentTime = Date.now() / 1000;
        
        if (decoded.exp > currentTime) {
          setUser({
            id: decoded.user_id || decoded.sub,
            email: decoded.email,
            token: token
          });
          // Check compliance access after setting user
          await checkComplianceAccess(token);
        } else {
          // Token expired
          Cookies.remove('login_jwt');
          setUser(null);
          setHasComplianceAccess(false);
        }
      } else {
        setHasComplianceAccess(false);
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      Cookies.remove('login_jwt');
      setUser(null);
      setHasComplianceAccess(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (token) => {
    try {
      Cookies.set('login_jwt', token, {
        sameSite: 'none',
        secure: true
      });
      
      const decoded = jwt_decode(token);
      setUser({
        id: decoded.user_id || decoded.sub,
        email: decoded.email,
        token: token
      });
      
      // Check compliance access after login
      await checkComplianceAccess(token);
      
      return true;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const logout = () => {
    Cookies.remove('login_jwt');
    setUser(null);
    setHasComplianceAccess(false);
    setHasComplianceLicense(false);
  };

  const value = {
    user,
    isAuthenticated: !!user,
    hasComplianceAccess,
    hasComplianceLicense,
    checkingAccess,
    loading,
    login,
    logout,
    getAuthToken: () => user?.token,
    checkAuthStatus,
    checkComplianceAccess
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};